import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { ReservationService } from "app/services";
import { errorMsg, successMsg } from "app/utils/MessageUtils";
import { getApiErrorMessage } from "app/utils/getApiErrorMessage";
import { filterSchedules } from "./filter-schedules.slice";
import store from "../../../store";

/* ---------- Helpers ---------- */

// allow undefined just in case
const isSuccessStatus = (status?: number) =>
  typeof status === "number" && status >= 200 && status < 300;

const refreshFilters = (dispatch: Dispatch) => {
  const filters = store.getState().schedules.filterSchedules;
  dispatch(
    //@ts-ignore
    filterSchedules({
      ...filters,
      page: 0,
    })
  );
};

const afterSuccess = (
  dispatch: Dispatch,
  toastTextEn: string,
  toastTextSq: string,
  onSuccess?: Function
) => {
  dispatch(setSuccess(true));
  dispatch(
    successMsg(
      localStorage.getItem("i18nextLng") === "en"
        ? toastTextEn
        : toastTextSq
    )
  );
  if (onSuccess) onSuccess();
  dispatch(setError(""));
  refreshFilters(dispatch);
};

const handleError = (
  dispatch: Dispatch,
  err: unknown,
  onError?: Function
) => {
  const msg = getApiErrorMessage(err, "Unexpected error");
  dispatch(errorMsg(msg));
  if (onError) onError(err);
  dispatch(setError(msg));
};

/* ---------- Params ---------- */

interface UpdatePassengerStateParams {
  id: string;
  body: any;
  onSuccess?: () => void;
  onError?: (err?: unknown) => void;
}

interface UpdateStatusPassenger {
  reservationId: string;
  travelerId: string;
  status: boolean;
  onSuccess?: () => void;
  onError?: (err?: unknown) => void;
}

/* ---------- Thunks ---------- */

export const updateStatusPassenger =
  (params: UpdateStatusPassenger) => async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const res = await ReservationService.updateStatusPassenger(
        params.reservationId,
        params.travelerId,
        params.status
      );

      if (isSuccessStatus(res?.status)) {
        afterSuccess(
          dispatch,
          "Passenger changed successfully!",
          "Pasagjeri u ndryshua me sukses!",
          params.onSuccess
        );
      } else {
        handleError(dispatch, res, params.onError);
      }
    } catch (err) {
      handleError(dispatch, err, params.onError);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updatePassenger =
  (params: UpdatePassengerStateParams) => async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const res = await ReservationService.updatePassenger(
        params.id,
        params.body
      );

      if (isSuccessStatus(res?.status)) {
        afterSuccess(
          dispatch,
          "Reservation changed successfully!",
          "Rezervimi u ndryshua me sukses!",
          params.onSuccess
        );
      } else {
        handleError(dispatch, res, params.onError);
      }
    } catch (err) {
      handleError(dispatch, err, params.onError);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updatePassengerBasicInfo =
  (params: UpdatePassengerStateParams) => async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const res = await ReservationService.updatePassengerBasicInfo(
        params.id,
        params.body
      );

      if (isSuccessStatus(res?.status)) {
        afterSuccess(
          dispatch,
          "Passenger changed successfully!",
          "Pasagjeri u ndryshua me sukses!",
          params.onSuccess
        );
      } else {
        handleError(dispatch, res, params.onError);
      }
    } catch (err) {
      handleError(dispatch, err, params.onError);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updatePassengerDepartureFlight =
  (params: UpdatePassengerStateParams) => async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const res = await ReservationService.updateDepartureFlight(
        params.id,
        params.body
      );

      if (isSuccessStatus(res?.status)) {
        afterSuccess(
          dispatch,
          "Flight changed successfully!",
          "Fluturimi u ndryshua me sukses!",
          params.onSuccess
        );
      } else {
        handleError(dispatch, res, params.onError);
      }
    } catch (err) {
      handleError(dispatch, err, params.onError);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updatePassengerDeparturePrice =
  (params: UpdatePassengerStateParams) => async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const res = await ReservationService.updateDepartureFlightPrice(
        params.id,
        params.body
      );

      if (isSuccessStatus(res?.status)) {
        afterSuccess(
          dispatch,
          "Price changed successfully!",
          "Çmimi u ndryshua me sukses!",
          params.onSuccess
        );
      } else {
        handleError(dispatch, res, params.onError);
      }
    } catch (err) {
      handleError(dispatch, err, params.onError);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updatePassengerReturnFlight =
  (params: UpdatePassengerStateParams) => async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const res = await ReservationService.updateReturnFlight(
        params.id,
        params.body
      );

      if (isSuccessStatus(res?.status)) {
        afterSuccess(
          dispatch,
          "Flight changed successfully!",
          "Fluturimi u ndryshua me sukses!",
          params.onSuccess
        );
      } else {
        handleError(dispatch, res, params.onError);
      }
    } catch (err) {
      handleError(dispatch, err, params.onError);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updatePassengerReturnPrice =
  (params: UpdatePassengerStateParams) => async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const res = await ReservationService.updateReturnFlightPrice(
        params.id,
        params.body
      );

      if (isSuccessStatus(res?.status)) {
        afterSuccess(
          dispatch,
          "Price changed successfully!",
          "Çmimi u ndryshua me sukses!",
          params.onSuccess
        );
      } else {
        handleError(dispatch, res, params.onError);
      }
    } catch (err) {
      handleError(dispatch, err, params.onError);
    } finally {
      dispatch(setLoading(false));
    }
  };

/* ---------- Slice ---------- */

interface UpdatePassengerSlice {
  success: boolean;
  error: string;
  loading: boolean;
}

const initialState: UpdatePassengerSlice = {
  error: "",
  success: false,
  loading: false,
};

const stateSlice = createSlice({
  name: "schedules/updatePassenger", // removed trailing space
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.success = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
});

export const { setLoading, setSuccess, setError } = stateSlice.actions;
export default stateSlice.reducer;
